import { Events } from "./Events";
import { Region } from "./Region";
import { _lcs, locale } from "./../i18n";

/**
 * Planner — the guest-app's view of the trip planner.
 *
 * Same division of labour as core/Events.js and core/Region.js: the API returns
 * data (ids, ISO dates, enum keys) and this turns it into the German wording,
 * the day rails and the row view models the planner screen renders. What is new
 * here is that it also WRITES — the planner is the first guest-facing screen
 * that changes something on the server, so this class is where "what the guest
 * sees" and "what the server was told" are kept from drifting apart.
 *
 * Every mutation therefore answers with the plan as the server now has it, and
 * this class replaces its state from that answer rather than patching its own
 * copy. Optimistic local edits are re-applied on top for the one operation
 * where the wait would be visible — dragging a row — and reconciled when the
 * response lands (see commitLayout).
 *
 * Needs a confirmed account: a plan belongs to a person (see App.hasVerifiedAccount).
 */
export class Planner
{
    /**
     * kind -> how a row is worded and tinted when no live record resolves. The
     * labels are German source strings that double as their dictionary keys —
     * a class constant is evaluated before I18n exists, so every reader
     * translates them (`_lcs( kind.label )`).
     */
    static KINDS = {
        checkin  : { label : "Aufenthalt",       icon : "key-round", tone : "green" },
        checkout : { label : "Aufenthalt",       icon : "door-open", tone : "green" },
        event    : { label : "Veranstaltung",    icon : "calendar",  tone : "green" },
        gastro   : { label : "Gastronomie",      icon : "utensils",  tone : "sand" },
        poi      : { label : "Sehenswürdigkeit", icon : "landmark",  tone : "sky" },
        tour     : { label : "Tour",             icon : "route",     tone : "sky" }
    };


    constructor( app )
    {
        this.app = app;

        // The guest's plans at this house (list payload: no items).
        this.plans = [];
        this.plansLoaded = false;
        this.plansFailed = false;

        // The open plan, fully resolved (items + suggestions).
        this.plan = null;

        this._pending = null;
    }

    /* -------------------------------- Loading -------------------------------- */

    /** Can this guest plan at all — a confirmed account and a resolved house. */
    isAvailable()
    {
        return this.app.hasVerifiedAccount() && !!this.app.accommodation;
    }

    /** The plan the guest was last looking at, so returning lands where they left. */
    getActivePlanId()
    {
        return this.app.storage.load( "activePlanId", null );
    }

    setActivePlanId( planId )
    {
        this.app.storage.save( "activePlanId", planId || null );
    }

    /**
     * The guest's plans at this house. Concurrent callers share one request;
     * never throws — a failed load flags `plansFailed` so the screen can say so
     * instead of showing an empty list as if the guest had never made one.
     */
    async loadPlans( force = false )
    {
        if ( !this.isAvailable() )
        {
            this.plansLoaded = true;
            return this.plans;
        }
        if ( !force && this.plansLoaded )
        {
            return this.plans;
        }
        if ( this._pending )
        {
            return await this._pending;
        }

        this._pending = this._fetchPlans().finally( () => { this._pending = null; } );
        return await this._pending;
    }

    async _fetchPlans()
    {
        try
        {
            const { result } = await this.app.api.get(
                "/plan?accommodationId=" + encodeURIComponent( this.app.accommodation.id ) );

            if ( result && 200 === +result.status && Array.isArray( result.data ) )
            {
                this.plans = result.data;
                this.plansLoaded = true;
                this.plansFailed = false;
                return this.plans;
            }
        }
        catch ( e )
        {
            console.error( e );
        }

        this.plansFailed = true;
        this.plansLoaded = true;
        return this.plans;
    }

    /**
     * Open one plan — or, with no id, the one to show by default: the plan the
     * guest last had open, else the one whose stay is happening or comes next,
     * else the most recent. A guest with an old trip and an upcoming one means
     * the upcoming one.
     */
    async open( planId = null )
    {
        await this.loadPlans();

        const id = planId || this._defaultPlanId();
        if ( !id )
        {
            this.plan = null;
            return null;
        }

        try
        {
            const { result } = await this.app.api.get( "/plan/" + encodeURIComponent( id ) );
            if ( result && 200 === +result.status && result.data )
            {
                this.plan = result.data;
                this.setActivePlanId( this.plan.id );
                return this.plan;
            }
        }
        catch ( e )
        {
            console.error( e );
        }

        // Gone (deleted on another device, or swept by the retention job).
        if ( this.getActivePlanId() === id )
        {
            this.setActivePlanId( null );
        }
        this.plans = this.plans.filter( ( plan ) => plan.id !== id );
        this.plan = null;
        return null;
    }

    _defaultPlanId()
    {
        if ( this.plans.length === 0 )
        {
            return null;
        }

        const remembered = this.getActivePlanId();
        if ( remembered && this.plans.some( ( plan ) => plan.id === remembered ) )
        {
            return remembered;
        }

        // The list arrives newest-stay-first, so the last plan that has not
        // ended yet is the nearest upcoming (or current) one.
        const today = Planner.today();
        const live = this.plans.filter( ( plan ) => plan.endDate >= today );
        return live.length > 0 ? live[ live.length - 1 ].id : this.plans[ 0 ].id;
    }

    /* -------------------------------- Writing -------------------------------- */

    /**
     * One write against the API.
     *
     * IF.RestApi REJECTS on a non-2xx response (see states/auth/LoginState),
     * carrying the status on the error — so the failure path is the catch, not
     * a status check. Everything below throws an Error whose message is already
     * the German sentence the caller shows; no screen has to know what an HTTP
     * status is.
     */
    async _write( method, endpoint, body, fallback )
    {
        try
        {
            const { result } = method === "delete"
                ? await this.app.api.delete( endpoint )
                : await this.app.api[ method ]( endpoint, body );

            const status = result ? +result.status : 0;
            if ( status === 200 || status === 201 )
            {
                return result.data;
            }
            throw new Error( fallback );
        }
        catch ( e )
        {
            console.error( e );
            throw new Error( Planner.messageForStatus( e, fallback ) );
        }
    }

    async create( { name, startDate, endDate } )
    {
        this.plan = await this._write( "post", "/plan", {
            "accommodationId" : this.app.accommodation.id,
            "name" : name,
            "startDate" : startDate,
            "endDate" : endDate
        }, _lcs( "Der Reiseplan konnte nicht erstellt werden." ) );

        this.setActivePlanId( this.plan.id );
        await this.loadPlans( true );
        return this.plan;
    }

    async update( planId, changes )
    {
        this.plan = await this._write( "patch", "/plan/" + encodeURIComponent( planId ), changes,
            _lcs( "Der Reiseplan konnte nicht gespeichert werden." ) );

        await this.loadPlans( true );
        return this.plan;
    }

    async remove( planId )
    {
        await this._write( "delete", "/plan/" + encodeURIComponent( planId ), null,
            _lcs( "Der Reiseplan konnte nicht gelöscht werden." ) );

        if ( this.getActivePlanId() === planId )
        {
            this.setActivePlanId( null );
        }
        if ( this.plan && this.plan.id === planId )
        {
            this.plan = null;
        }
        await this.loadPlans( true );
        return true;
    }

    /**
     * "Zum Planer hinzufügen" — put a record in a plan's unsorted list.
     *
     * Called from the detail screens as well as from the planner's own
     * suggestions, so it takes a plan id rather than assuming the open one.
     *
     * @returns {Promise<{ item: object, isNew: boolean }>} `isNew` false means
     *          it was already in the plan — the caller says so rather than
     *          claiming to have added it twice.
     */
    async addItem( planId, kind, recordId )
    {
        const item = await this._write( "post",
            "/plan/" + encodeURIComponent( planId ) + "/item",
            { "kind" : kind, "recordId" : recordId },
            _lcs( "Der Eintrag konnte nicht hinzugefügt werden." ) );

        if ( this.plan && this.plan.id === planId )
        {
            this._mergeItem( item );
        }
        if ( item.isNew !== false )
        {
            this.app.tracker.track( "plan_add" );
        }
        return { "item" : item, "isNew" : item.isNew !== false };
    }

    async removeItem( planId, itemId )
    {
        await this._write( "delete",
            "/plan/" + encodeURIComponent( planId ) + "/item/" + encodeURIComponent( itemId ), null,
            _lcs( "Der Eintrag konnte nicht entfernt werden." ) );

        if ( this.plan && this.plan.id === planId )
        {
            this.plan.items = ( this.plan.items || [] ).filter( ( item ) => item.id !== itemId );
        }
        return true;
    }

    /** Move or annotate one entry — the chevrons and the day picker. */
    async patchItem( planId, itemId, changes )
    {
        const item = await this._write( "patch",
            "/plan/" + encodeURIComponent( planId ) + "/item/" + encodeURIComponent( itemId ),
            changes, _lcs( "Der Eintrag konnte nicht verschoben werden." ) );

        if ( this.plan && this.plan.id === planId )
        {
            this._mergeItem( item );
        }
        return item;
    }

    /**
     * Commit a whole board arrangement after a drag.
     *
     * The local copy is updated FIRST, from the placements the screen is
     * already showing: the rows have visibly moved, and re-rendering them back
     * to their old positions for the length of a round trip is the one thing
     * that would make the drag feel broken. The response then replaces the
     * state outright, so a placement the server rejected (a day that no longer
     * exists, an entry deleted on another device) corrects itself on the next
     * paint rather than persisting as a local fiction.
     */
    async commitLayout( planId, placements )
    {
        this._applyLayout( placements );

        const data = await this._write( "put",
            "/plan/" + encodeURIComponent( planId ) + "/order", { "placements" : placements },
            _lcs( "Die Reihenfolge konnte nicht gespeichert werden." ) );

        if ( this.plan && this.plan.id === planId )
        {
            // Suggestions are not part of this payload — keep the ones the
            // screen is already showing rather than blanking the strip.
            this.plan = { ...data, "suggestions" : this.plan.suggestions };
        }
        return this.plan;
    }

    /** Refresh the suggestion strip without re-fetching every entry. */
    async reloadSuggestions( planId )
    {
        try
        {
            const { result } = await this.app.api.get(
                "/plan/" + encodeURIComponent( planId ) + "/recommendations" );

            if ( result && 200 === +result.status && result.data &&
                 this.plan && this.plan.id === planId )
            {
                this.plan.suggestions = result.data;
            }
        }
        catch ( e )
        {
            // The strip keeps whatever it had; nothing the guest needs told.
            console.error( e );
        }
        return this.plan ? this.plan.suggestions : null;
    }

    _mergeItem( item )
    {
        const items = this.plan.items || ( this.plan.items = [] );
        const index = items.findIndex( ( existing ) => existing.id === item.id );
        if ( index === -1 )
        {
            items.push( item );
        }
        else
        {
            items[ index ] = item;
        }
    }

    _applyLayout( placements )
    {
        if ( !this.plan || !Array.isArray( this.plan.items ) )
        {
            return;
        }

        const byId = new Map( placements.map( ( placement ) => [ placement.itemId, placement ] ) );
        this.plan.items.forEach( ( item ) =>
        {
            const placement = byId.get( item.id );
            if ( placement )
            {
                item.dayDate = placement.dayDate;
                item.sortOrder = placement.sortOrder;
            }
        } );
    }

    /**
     * The "Zum Planer hinzufügen" button on a detail screen.
     *
     * Everything that can go wrong before the request is a different answer,
     * and each of them is a place to send the guest rather than an error:
     *
     *   • no confirmed account  → the gate, which explains what is missing
     *   • no plan yet           → the planner's create form, holding on to what
     *                             they were looking at so it is added the
     *                             moment the plan exists
     *   • otherwise             → straight into the plan they last had open
     *
     * @returns {Promise<('added'|'known'|'redirected'|'failed')>} what happened,
     *          so the caller can word its own confirmation.
     */
    async addFromDetail( kind, recordId, { onNeedsPlan = null } = {} )
    {
        if ( !this.app.hasVerifiedAccount() )
        {
            this.app.router.redirect( "/verify-account?from=plan" );
            return "redirected";
        }
        if ( !this.app.accommodation )
        {
            this.app.view.toast( _lcs( "Für diese Unterkunft ist der Planer nicht verfügbar." ), "info" );
            return "failed";
        }

        await this.loadPlans();

        if ( this.plans.length === 0 )
        {
            if ( onNeedsPlan ) onNeedsPlan();
            this.app.router.redirect( "/plan" );
            return "redirected";
        }

        const planId = this.plan ? this.plan.id : this._defaultPlanId();

        try
        {
            const { item, isNew } = await this.addItem( planId, kind, recordId );
            this.app.view.toast( Planner.addedText( item, isNew ), isNew ? "success" : "info" );
            return isNew ? "added" : "known";
        }
        catch ( e )
        {
            this.app.view.toast( e.message, "error" );
            return "failed";
        }
    }

    /**
     * The confirmation after an add. An event with a date inside the stay
     * went straight onto its day (see the API's PlanItemPostRequest) — the
     * toast says which, so the guest does not go looking for it in the list.
     */
    static addedText( item, isNew )
    {
        if ( !isNew )
        {
            return _lcs( "Steht schon in deinem Planer." );
        }
        return item && item.dayDate
            ? _lcs( "Eingeplant für {day}", { day : Planner.dayText( item.dayDate ) } )
            : _lcs( "Gemerkt — noch ohne Tag." );
    }

    /**
     * Would putting this entry on `dayDate` take an event away from its date?
     * Only for events whose dates pin them to days of the stay (`eventDays`,
     * see the API's PlanContentService._fixedDates). Back to the list without
     * a day is never a conflict — nothing is claimed about when.
     *
     * @returns {(string|null)} the day the event really is on, or null.
     */
    getDateConflict( itemId, dayDate )
    {
        const item = ( this.plan?.items || [] ).find( ( entry ) => entry.id === itemId );
        const days = ( item && item.eventDays ) || [];
        if ( !dayDate || days.length === 0 || days.includes( dayDate ) )
        {
            return null;
        }
        return days[ 0 ];
    }

    /* ------------------------------ Presentation ----------------------------- */

    /**
     * The open plan as the screen renders it: the list without a day, then one
     * section per day of the stay — empty days included, because an empty day
     * is where a guest drops the next thing and a day that is not drawn cannot
     * be dropped on.
     *
     * Each movable row learns whether it is first or last among the movable
     * rows of its list, so the arrows that would do nothing are drawn
     * disabled rather than live (Funktionstest F13).
     */
    getBoard()
    {
        const plan = this.plan;
        if ( !plan )
        {
            return null;
        }

        const items = ( plan.items || [] ).map( ( item ) => Planner.toRow( item ) );
        const inDay = ( day ) =>
        {
            const list = items
                .filter( ( row ) => row.dayDate === day )
                .sort( ( a, b ) => a.sortOrder - b.sortOrder );
            const movable = list.filter( ( row ) => !row.isFixed );
            movable.forEach( ( row, index ) =>
            {
                row.canUp = index > 0;
                row.canDown = index < movable.length - 1;
            } );
            return list;
        };

        return {
            "id" : plan.id,
            "name" : plan.name,
            "startDate" : plan.startDate,
            "endDate" : plan.endDate,
            "isPast" : plan.isPast === true,
            "rangeText" : Planner.rangeText( plan.startDate, plan.endDate ),
            "nightsText" : Planner.nightsText( plan.nights ),
            "unsorted" : inDay( null ),
            "days" : ( plan.days || [] ).map( ( day, index ) => ( {
                "date" : day,
                "index" : index + 1,
                "label" : _lcs( "Tag {n}", { n : index + 1 } ),
                "dateText" : Planner.dayText( day ),
                "isToday" : day === Planner.today(),
                "items" : inDay( day )
            } ) ),
            "suggestions" : Planner.toSuggestions( plan.suggestions )
        };
    }

    /** The day picker offered on a row ("auf Tag 2 schieben"). */
    getDayOptions()
    {
        const plan = this.plan;
        if ( !plan )
        {
            return [];
        }
        return ( plan.days || [] ).map( ( day, index ) => ( {
            "date" : day,
            "label" : _lcs( "Tag {n}", { n : index + 1 } ) + " · " + Planner.dayText( day )
        } ) );
    }

    /**
     * One stored entry as a planner row.
     *
     * The live record supplies the wording where there is one; the snapshot the
     * entry was saved with is the fallback, which is what keeps a row readable
     * when the regional import has dropped the record mid-stay (see
     * model/PlanItem on the API side).
     */
    static toRow( item )
    {
        const kind = Planner.KINDS[ item.kind ] || Planner.KINDS.event;
        const card = Planner.toCard( item );

        return {
            "id" : item.id,
            "kind" : item.kind,
            "recordId" : item.recordId,
            "dayDate" : item.dayDate === undefined ? null : item.dayDate,
            "sortOrder" : +item.sortOrder || 0,
            "isFixed" : item.isFixed === true,
            "isUnavailable" : item.isUnavailable === true,
            // A stay marker's title is the API's German wording, a dictionary key.
            "title" : ( card && card.title ) ||
                ( item.title && item.isFixed === true ? _lcs( item.title ) : item.title ) || _lcs( kind.label ),
            "meta" : Planner.metaText( item, card, kind ),
            "note" : item.note || "",
            "icon" : ( card && card.icon ) || item.icon || kind.icon,
            "tone" : kind.tone,
            "image" : ( card && card.image ) || "",
            // Where tapping the row goes; the stay markers lead nowhere.
            "route" : Planner.routeFor( item ),
            // Arrows that would do nothing are disabled — set by getBoard.
            "canUp" : false,
            "canDown" : false,
            "dateNote" : Planner.dateNote( item )
        };
    }

    /**
     * The line that says an event has a date of its own: on a day other than
     * that date, a warning ("Findet am Fr, 14.06. statt"); without a day yet,
     * a hint where it belongs. Nothing when it sits on its day, or for
     * anything without a fixed date.
     *
     * @returns {({ text: string, warn: boolean }|null)}
     */
    static dateNote( item )
    {
        const days = item.eventDays || [];
        if ( days.length === 0 || ( item.dayDate && days.includes( item.dayDate ) ) )
        {
            return null;
        }
        const day = Planner.dayText( days[ 0 ] );
        return item.dayDate
            ? { "text" : _lcs( "Findet am {day} statt", { day : day } ), "warn" : true }
            : { "text" : _lcs( "Termin: {day}", { day : day } ), "warn" : false };
    }

    /** The resolved record as the same card shape the rest of the app uses. */
    static toCard( item )
    {
        if ( !item.record )
        {
            return null;
        }
        if ( item.kind === "event" )
        {
            return Events.toCard( item.record );
        }
        if ( item.kind === "tour" )
        {
            return Region.tourToCard( item.record );
        }
        if ( item.kind === "gastro" || item.kind === "poi" )
        {
            return Region.placeToCard( item.record );
        }
        return null;
    }

    /**
     * The line under a row's title.
     *
     * A time the guest set themselves wins over anything the record says: they
     * put it there, and it is usually a table booking or a meeting time the
     * feed knows nothing about.
     */
    static metaText( item, card, kind )
    {
        const parts = [];

        if ( item.startTime )
        {
            parts.push( _lcs( "{time} Uhr", { time : item.startTime } ) );
        }
        else if ( item.kind === "event" && card && card.time )
        {
            parts.push( _lcs( "{time} Uhr", { time : card.time } ) );
        }

        if ( item.isUnavailable )
        {
            parts.push( _lcs( "Nicht mehr verfügbar" ) );
        }
        else if ( card && card.cat )
        {
            parts.push( card.cat );
        }
        else
        {
            parts.push( _lcs( kind.label ) );
        }

        const distance = card && ( card.dist || card.distance );
        if ( distance )
        {
            parts.push( distance );
        }

        return parts.join( " · " );
    }

    static routeFor( item )
    {
        if ( !item.recordId || item.isUnavailable )
        {
            return "";
        }
        if ( item.kind === "event" )
        {
            return "/events/" + item.recordId;
        }
        if ( item.kind === "tour" )
        {
            return "/region/tour/" + item.recordId;
        }
        return "/region/place/" + item.recordId;
    }

    /**
     * The suggestion strip, flattened and interleaved.
     *
     * Round-robin across the kinds rather than concatenated, for the same
     * reason core/Region.js does it: a strip that is six restaurants followed
     * by nothing else misrepresents what is on offer.
     */
    static toSuggestions( suggestions )
    {
        if ( !suggestions )
        {
            return [];
        }

        const order = [ "event", "gastro", "poi", "tour" ];
        const buckets = order.map( ( kind ) => ( suggestions[ kind ] || [] )
            .map( ( entry ) => Planner.toSuggestion( entry ) ) );

        const mixed = [];
        const longest = Math.max( ...buckets.map( ( bucket ) => bucket.length ), 0 );
        for ( let i = 0; i < longest; i++ )
        {
            buckets.forEach( ( bucket ) => { if ( bucket[ i ] ) mixed.push( bucket[ i ] ); } );
        }
        return mixed;
    }

    static toSuggestion( entry )
    {
        const kind = Planner.KINDS[ entry.kind ] || Planner.KINDS.event;
        const card = Planner.toCard( entry );

        const meta = [];
        if ( entry.dayDate )
        {
            meta.push( Planner.dayText( entry.dayDate ) );
        }
        if ( card && card.time && entry.kind === "event" )
        {
            meta.push( _lcs( "{time} Uhr", { time : card.time } ) );
        }
        meta.push( ( card && card.cat ) || _lcs( kind.label ) );

        return {
            "kind" : entry.kind,
            "recordId" : entry.recordId,
            "title" : ( card && card.title ) || _lcs( kind.label ),
            "meta" : meta.join( " · " ),
            "icon" : ( card && card.icon ) || kind.icon,
            "tone" : kind.tone
        };
    }

    /* -------------------------------- Formatting ----------------------------- */

    static today()
    {
        const now = new Date();
        return [
            now.getFullYear(),
            String( now.getMonth() + 1 ).padStart( 2, "0" ),
            String( now.getDate() ).padStart( 2, "0" )
        ].join( "-" );
    }

    /**
     * `YYYY-MM-DD` -> a Date at LOCAL midnight.
     *
     * `new Date( "2026-09-21" )` parses as UTC and then displays in local time,
     * which in Germany is still the 21st but in any negative offset is the
     * 20th. Building it from the parts keeps the day the guest picked.
     */
    static toDate( iso )
    {
        const [ year, month, day ] = String( iso || "" ).split( "-" ).map( Number );
        return new Date( year, ( month || 1 ) - 1, day || 1 );
    }

    /** "Mo, 21.09." — the day header on the board, worded per language. */
    static dayText( iso )
    {
        const date = Planner.toDate( iso );
        if ( isNaN( date.getTime() ) )
        {
            return "";
        }
        return date.toLocaleDateString( locale(), {
            weekday : "short",
            day     : "2-digit",
            month   : "2-digit"
        } );
    }

    /** "21.–24. September 2026" — collapsed where the parts are shared. */
    static rangeText( startIso, endIso )
    {
        const start = Planner.toDate( startIso );
        const end = Planner.toDate( endIso );
        if ( isNaN( start.getTime() ) || isNaN( end.getTime() ) )
        {
            return "";
        }

        // Intl does the collapsing, and does it per language: German drops the
        // shared month, English keeps it in front ("September 21 – 24, 2026").
        // Hand-built rules only ever produced the German shape.
        const formatter = new Intl.DateTimeFormat( locale(), {
            day : "numeric", month : "long", year : "numeric"
        } );

        if ( startIso !== endIso && "function" === typeof formatter.formatRange )
        {
            return formatter.formatRange( start, end );
        }
        return startIso === endIso
            ? formatter.format( start )
            : formatter.format( start ) + " – " + formatter.format( end );
    }

    static nightsText( nights )
    {
        const count = +nights || 0;
        // `count` picks the plural form, `n` is what gets printed — Russian and
        // Czech need three forms where German needs two.
        return _lcs( "{count} Nächte", {
            count : count,
            n     : count.toLocaleString( locale() )
        } );
    }

    /** The name a plan is offered when the guest has not typed one. */
    defaultPlanName()
    {
        const house = this.app.accommodation?.name;
        return house
            ? _lcs( "Meine Reise · {house}", { house : house } )
            : _lcs( "Mein Reiseplan" );
    }

    /**
     * A failed write as something a guest can read.
     *
     * The API's own message is deliberately NOT surfaced: it is developer-facing
     * English aimed at whoever wrote the client ("Day 2026-10-30 is outside this
     * plan's stay"), and every case a guest can actually reach is either caught
     * in the form first or covered by one of the sentences below.
     */
    static messageForStatus( error, fallback )
    {
        switch ( +( error && error.status ) )
        {
            case 401:
            case 403: return _lcs( "Dafür fehlt dir die Berechtigung. Melde dich neu an." );
            case 404: return _lcs( "Dieser Eintrag ist nicht mehr verfügbar." );
            case 409: return _lcs( "Das geht nicht mehr — du hast das Maximum erreicht." );
            default:  return fallback;
        }
    }
}
